import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { genres } from "@/mocks/genres";

export default function Navbar() {
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const [scrolled, setScrolled] = useState(!isHome);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24 || !isHome);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [isHome]);

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const kw = keyword.trim();
    if (kw) {
      navigate(`/category/all?q=${encodeURIComponent(kw)}`);
      setKeyword("");
      setMenuOpen(false);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/90 backdrop-blur-md shadow-soft"
          : "bg-transparent"
      }`}
    >
      <nav className="container-x flex h-16 items-center justify-between gap-4">
        <Link
          to="/"
          className="flex shrink-0 items-center gap-2"
          aria-label="91韩漫画 首页"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-book-open-fill text-lg" />
          </span>
          <span
            className={`font-heading text-xl font-bold tracking-tight ${
              scrolled ? "text-foreground-950" : "text-background-50"
            }`}
          >
            91韩漫画
          </span>
        </Link>

        <ul className="hidden items-center gap-1 md:flex">
          <li>
            <Link
              to="/"
              className={`rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-background-100/60 ${
                scrolled
                  ? "text-foreground-800 hover:text-primary-600"
                  : "text-background-50/90 hover:bg-background-50/20 hover:text-background-50"
              }`}
            >
              首页
            </Link>
          </li>
          {genres.map((g) => (
            <li key={g.id}>
              <Link
                to={`/category/${g.id}`}
                className={`rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-background-100/60 ${
                  scrolled
                    ? "text-foreground-800 hover:text-primary-600"
                    : "text-background-50/90 hover:bg-background-50/20 hover:text-background-50"
                }`}
              >
                {g.name}漫
              </Link>
            </li>
          ))}
          <li>
            <Link
              to="/about"
              className={`rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-background-100/60 ${
                scrolled
                  ? "text-foreground-800 hover:text-primary-600"
                  : "text-background-50/90 hover:bg-background-50/20 hover:text-background-50"
              }`}
            >
              关于我们
            </Link>
          </li>
        </ul>

        <div className="hidden items-center gap-3 lg:flex">
          <form onSubmit={onSearch} className="relative">
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索漫画名称 / 作者"
              className="h-9 w-56 rounded-md border border-background-200 bg-background-100/80 pl-9 pr-3 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-300 focus:bg-background-50"
            />
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400" />
          </form>
          <Link to="/faq" className="btn-ghost h-9 px-3 text-sm">
            常见问题
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className={`flex h-9 w-9 items-center justify-center rounded-md text-xl transition-colors md:hidden ${
            scrolled ? "text-foreground-900" : "text-background-50"
          }`}
          aria-label="打开菜单"
        >
          <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"}`} />
        </button>
      </nav>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-4 pt-2 md:hidden">
          <form onSubmit={onSearch} className="relative mb-2">
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索漫画名称 / 作者"
              className="h-10 w-full rounded-md border border-background-200 bg-background-100/80 pl-9 pr-3 text-sm text-foreground-800 outline-none focus:border-primary-300"
            />
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400" />
          </form>
          <ul className="flex flex-col gap-1">
            {[
              { to: "/", label: "首页" },
              ...genres.map((g) => ({ to: `/category/${g.id}`, label: `${g.name}漫` })),
              { to: "/faq", label: "常见问题" },
              { to: "/about", label: "关于我们" },
            ].map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  onClick={() => setMenuOpen(false)}
                  className="block rounded-md px-3 py-2 text-sm font-medium text-foreground-800 hover:bg-background-100"
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </header>
  );
}